import { money } from '../Plans.js';

/**
 * HOW A CREDIT FIGURE IS PRINTED — and the only arithmetic it ever sees.
 *
 * Every figure arrives as integer euro CENTS that the backend has already
 * rounded for display (design C.2: a balance floored, an estimate's minimum
 * floored and its maximum ceiled, a charge rounded half up). The SPA prints
 * them through `money()`, the workspace's one money formatter, and does not
 * add, subtract, compare for size or round any of them: a balance worked out in
 * the browser is a second answer that drifts from the ledger's.
 *
 * The contract has one currency (`docs/contracts/ai-credits.json`, `currency`).
 */
export const CREDIT_CURRENCY = 'EUR';

/** A balance, an estimate or a charge: always two decimals ("€50.00", "€0.85"). */
export function creditAmount(cents: number): string {
  return money(cents, CREDIT_CURRENCY, { cents: true });
}

/** A ledger line: "+€10.00" for credits in, "-€0.62" for a charge. */
export function signedCreditAmount(cents: number): string {
  return money(cents, CREDIT_CURRENCY, { cents: true, signed: true });
}

/** A package's price, printed as a price: "€10". */
export function packagePrice(cents: number): string {
  return money(cents, CREDIT_CURRENCY);
}

/** "€0.35–€0.85", or one figure when the server's minimum and maximum are the same. */
export function creditRange(minCents: number, maxCents: number): string {
  return minCents === maxCents ? creditAmount(maxCents) : `${creditAmount(minCents)}–${creditAmount(maxCents)}`;
}

const PERCENT = new Intl.NumberFormat('en-IE', { style: 'percent', maximumFractionDigits: 2 });

/**
 * A VAT RATE as a percentage ("20%"). A rate, never an amount: the VAT itself
 * is the server's `taxCents`, printed as it came.
 */
export function vatRateLabel(bps: number): string {
  return PERCENT.format(bps / 10_000);
}

/** "1 page", "12 pages". */
export function pagesLabel(count: number): string {
  return `${String(count)} ${count === 1 ? 'page' : 'pages'}`;
}
